class MuseumHeroSlider {
  constructor(root) {
    if (root.museumHeroSlider) return root.museumHeroSlider;

    this.root = root;
    this.slides = Array.from(root.querySelectorAll('.museum-hero__slide'));
    this.dots = Array.from(root.querySelectorAll('[data-slide-to]'));
    this.previousButton = root.querySelector('.museum-hero__control--previous');
    this.nextButton = root.querySelector('.museum-hero__control--next');
    this.playToggle = root.querySelector('.museum-hero__play-toggle');
    this.status = root.querySelector('[aria-live]');
    this.autoplayEnabled = root.dataset.autoplay === 'true' && this.slides.length > 1;
    this.pauseOnHover = root.dataset.pauseOnHover === 'true';
    this.swipeEnabled = root.dataset.swipe === 'true';
    this.interval = Number(root.dataset.interval) || 6000;
    this.currentIndex = Math.max(this.slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
    this.timer = null;
    this.userPaused = !this.autoplayEnabled;
    this.editorPaused = false;
    this.touchStart = null;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    root.museumHeroSlider = this;
    this.bindEvents();
    this.showSlide(this.currentIndex, { announce: false });
    this.startAutoplay();
  }

  bindEvents() {
    this.previousButton?.addEventListener('click', () => this.showSlide(this.currentIndex - 1));
    this.nextButton?.addEventListener('click', () => this.showSlide(this.currentIndex + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.showSlide(Number(dot.dataset.slideTo))));
    this.playToggle?.addEventListener('click', () => this.toggleAutoplay());

    this.root.addEventListener('keydown', this.onKeydown);
    this.root.addEventListener('focusin', this.onFocusIn);
    this.root.addEventListener('focusout', this.onFocusOut);

    if (this.pauseOnHover) {
      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
    }

    if (this.swipeEnabled) {
      this.root.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.root.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.reducedMotionQuery.addEventListener?.('change', this.onReducedMotionChange);
  }

  onKeydown = (event) => {
    if (event.target.matches('input, textarea, select')) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showSlide(this.currentIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showSlide(this.currentIndex + 1);
    }
  };

  onFocusIn = () => this.stopAutoplay();

  onFocusOut = () => {
    window.setTimeout(() => {
      if (!this.root.contains(document.activeElement)) this.startAutoplay();
    }, 0);
  };

  onMouseEnter = () => this.stopAutoplay();
  onMouseLeave = () => this.startAutoplay();

  onTouchStart = (event) => {
    const touch = event.changedTouches[0];
    this.touchStart = { x: touch.screenX, y: touch.screenY };
  };

  onTouchEnd = (event) => {
    if (!this.touchStart) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.screenX - this.touchStart.x;
    const deltaY = touch.screenY - this.touchStart.y;
    this.touchStart = null;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.showSlide(this.currentIndex + (deltaX < 0 ? 1 : -1));
  };

  onVisibilityChange = () => {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  };

  onReducedMotionChange = () => {
    if (this.reducedMotionQuery.matches) this.stopAutoplay();
    else this.startAutoplay();
  };

  showSlide(index, options = {}) {
    if (!this.slides.length) return;
    const { announce = true } = options;
    const normalizedIndex = (index + this.slides.length) % this.slides.length;
    this.currentIndex = normalizedIndex;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === normalizedIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      this.setSlideFocusability(slide, isActive);
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === normalizedIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
      dot.setAttribute('aria-selected', String(isActive));
    });

    if (announce && this.status) {
      this.status.textContent = `Slide ${normalizedIndex + 1} of ${this.slides.length}`;
    }

    this.restartAutoplay();
  }

  setSlideFocusability(slide, isActive) {
    slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      if (isActive) {
        if (element.dataset.museumTabindex === '') element.removeAttribute('tabindex');
        else if (element.dataset.museumTabindex !== undefined) element.setAttribute('tabindex', element.dataset.museumTabindex);
      } else {
        if (element.dataset.museumTabindex === undefined) element.dataset.museumTabindex = element.getAttribute('tabindex') || '';
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  canAutoplay() {
    return this.autoplayEnabled && !this.userPaused && !this.editorPaused && !document.hidden && !this.reducedMotionQuery.matches;
  }

  startAutoplay() {
    if (!this.canAutoplay() || this.timer) return;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.showSlide(this.currentIndex + 1);
    }, this.interval);
    this.updatePlayToggle();
  }

  stopAutoplay() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
    this.updatePlayToggle();
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  toggleAutoplay() {
    this.editorPaused = false;
    this.userPaused = !this.userPaused;
    if (this.userPaused) this.stopAutoplay();
    else this.startAutoplay();
    this.updatePlayToggle();
  }

  updatePlayToggle() {
    if (!this.playToggle) return;
    const isPaused = !this.timer;
    this.playToggle.classList.toggle('is-paused', isPaused);
    this.playToggle.setAttribute('aria-pressed', String(isPaused));
    this.playToggle.setAttribute('aria-label', isPaused ? 'Play slideshow' : 'Pause slideshow');
    const label = this.playToggle.querySelector('.visually-hidden');
    if (label) label.textContent = isPaused ? 'Play slideshow' : 'Pause slideshow';
  }

  selectFromThemeEditor(slide) {
    const index = this.slides.indexOf(slide);
    if (index === -1) return;
    this.editorPaused = true;
    this.stopAutoplay();
    this.showSlide(index);
  }

  destroy() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.reducedMotionQuery.removeEventListener?.('change', this.onReducedMotionChange);
    delete this.root.museumHeroSlider;
  }
}

function initializeMuseumHeroSliders(container = document) {
  container.querySelectorAll?.('[data-museum-hero-slider]').forEach((root) => new MuseumHeroSlider(root));
  if (container.matches?.('[data-museum-hero-slider]')) new MuseumHeroSlider(container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeMuseumHeroSliders(), { once: true });
} else {
  initializeMuseumHeroSliders();
}

document.addEventListener('shopify:section:load', (event) => initializeMuseumHeroSliders(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('[data-museum-hero-slider]').forEach((root) => root.museumHeroSlider?.destroy());
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('.museum-hero__slide');
  slide?.closest('[data-museum-hero-slider]')?.museumHeroSlider?.selectFromThemeEditor(slide);
});
